import React, { useState, useEffect, useRef } from 'react';
import { DataProviderInstance } from '../../api/DataProvider';
import { GetTermsContentAPI } from '../../api/API';
import Loading from '../../components/loading/Loading';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import { parseTermsDocument } from './parseTermsDocument';
import TermsTableOfContents from './TermsTableOfContents';
import styles from './TermsPrivacy.module.css';

/**
 * Página completa dos Termos de Uso e da Política de Privacidade.
 *
 * O texto vem de public/data/termos_privacidade.html (pela API company/terms/get.php)
 * e é renderizado direto na página, com índice lateral das seções.
 */
const TermsPrivacy = () => {
  const [terms, setTerms] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [activeId, setActiveId] = useState('');
  const contentRef = useRef(null);
  const dataProvider = DataProviderInstance();

  useEffect(() => {
    setIsLoading(true);

    const handleResponse = (response) => {
      setIsLoading(false);

      if (response && response.success && response.content) {
        setTerms(parseTermsDocument(response.content));
      } else {
        setFailed(true);
      }
    };

    // Rota protegida usa o DataProvider; a pública chama a API direto.
    if (dataProvider) {
      dataProvider.GetTermsContent(handleResponse);
    } else {
      GetTermsContentAPI(handleResponse);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Destaca no índice a seção que está no topo da tela.
  useEffect(() => {
    if (!terms || !contentRef.current) return undefined;

    const headings = contentRef.current.querySelectorAll('h2[id]');
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((entry) => entry.isIntersecting);
        if (visible.length > 0) {
          setActiveId(visible[0].target.id);
        }
      },
      { rootMargin: '0px 0px -75% 0px' }
    );

    headings.forEach((heading) => observer.observe(heading));
    return () => observer.disconnect();
  }, [terms]);

  const handleNavigate = (id) => {
    const target = contentRef.current && contentRef.current.querySelector(`#${id}`);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveId(id);
    }
  };

  return (
    <div className="page-content">
      <div className={styles.page}>
        <header className={styles.hero}>
          <span className={styles.heroIcon}>
            <VerifiedUserOutlinedIcon />
          </span>
          <span className={styles.eyebrow}>Documento legal</span>
          <h1 className={styles.heroTitle}>Termos de Uso e Política de Privacidade</h1>
          <p className={styles.heroSubtitle}>
            As regras de uso da Gabriela e como cuidamos dos dados da sua clínica e dos seus pacientes.
          </p>
          {terms && terms.version && (
            <span className={styles.version}>{terms.version}</span>
          )}
        </header>

        {isLoading && (
          <div className={styles.loadingContainer}>
            <Loading size={50} />
          </div>
        )}

        {!isLoading && failed && (
          <div className={styles.emptyContent}>
            Não foi possível carregar os termos agora. Tente novamente em alguns instantes.
          </div>
        )}

        {!isLoading && terms && (
          <div className={styles.layout}>
            <aside className={styles.sidebar}>
              <TermsTableOfContents
                toc={terms.toc}
                activeId={activeId}
                onNavigate={handleNavigate}
              />
            </aside>

            <article
              ref={contentRef}
              className={styles.document}
              dangerouslySetInnerHTML={{ __html: terms.html }}
            />
          </div>
        )}

        <footer className={styles.footer}>
          <p>
            Dúvidas sobre os termos: <a href="mailto:suporte@gabriela.app.br">suporte@gabriela.app.br</a>
            {' · '}
            Dados pessoais: <a href="mailto:privacidade@gabriela.app.br">privacidade@gabriela.app.br</a>
          </p>
          <p>© {new Date().getFullYear()} Gabriela — Todos os direitos reservados.</p>
        </footer>
      </div>
    </div>
  );
};

export default TermsPrivacy;
